import {McpBillingService} from './service/mcp-billing.service';
import { McpProviderConnectionService } from './service/mcp-provider-connection.service';
import { McpAccountLinkService } from './service/mcp-account-link.service';
import { McpEmbedService } from './service/mcp-embed.service';
import { Component, OnInit } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { DeviceDetectorService } from "ngx-device-detector";
import { LocalStorageService } from "angular-web-storage";
import { ActivatedRoute, Router } from "@angular/router";
import { NgxSpinnerService } from "ngx-spinner";
import { LayoutRequest } from "./util/static-data";
import { DataService } from "./service/data.service";
import { environment } from "src/environments/environment";
import { MirrorService } from "./service/mirror.service";
import { DeviceStorageService } from "./service/device-storage.service";
import { PwaSerivce } from "./service/pwa.service";
import { tap } from "rxjs";
import { get } from "jquery";

export function isAndroidProfileAppLink(
  platform: string | null | undefined,
  pathname: string,
): boolean {
  return (
    platform === "android" && pathname.replace(/\/+$/, "") === "/user-profile"
  );
}

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  standalone: false,
})
export class AppComponent implements OnInit {
  title = "MangoDisplayPwaApp";
  deferredPrompt: any;
  showButton = false;
  newVariable: any = window.navigator;
  standalone = this.newVariable.standalone;
  userAgent = window.navigator.userAgent.toLocaleUpperCase();
  isAndroid = this.userAgent.indexOf("android") > -1;
  allowedOrigin = environment.portalBaseURL;
  conversionCode: any[] = [];

  constructor(
    public mcpEmbed: McpEmbedService,
    public mcpBilling: McpBillingService,
    public mcpProvider: McpProviderConnectionService,
    private mcpAccountLink: McpAccountLinkService,
    private route: Router,
    public translate: TranslateService,
    private deviceService: DeviceDetectorService,
    private storage: LocalStorageService,
    private loadingSpinner: NgxSpinnerService,
    private layoutRequest: LayoutRequest,
    private _dataService: DataService,
    private mirrorService: MirrorService,
    private deviceStorage: DeviceStorageService,
    private pwaService: PwaSerivce,
  ) {
    translate.addLangs(["en", "es", "nl", "pt"]);
    translate.setDefaultLang("en");
    const userLanguage = this.storage.get("userDetails")?.userLocalLanguage;
    translate.use(
      translate.getLangs().includes(userLanguage) ? userLanguage : "en",
    );
  }

  getFbp() {
    const match = document.cookie.match(/(^| )_fbp=([^;]+)/);
    return match ? match[2] : null;
  }

  /* Auto-open: launching the native app goes straight to the display
   * view when the user turned that on for the display this device owns. */
  private autoOpenOwnedDisplay(): boolean {
    try {
      if (!this.pwaService.isMangoDisplayApp()) {
        return false;
      }
      /* Only a genuine launch: the wrapper always opens the app at the
       * root URL. Any deeper path means this boot is a mid-session
       * reload (e.g. WKWebView reloading after its content process is
       * recycled) or a deep link such as an OAuth callback — those must
       * keep their route instead of being hijacked into the display. */
      if (window.location.pathname !== "/") {
        return false;
      }
      const userDetails = this.storage.get("userDetails");
      if (!userDetails || !userDetails.authToken) {
        return false;
      }
      const autoOpenMirror = this.deviceStorage.getAutoOpenMirror();
      const deviceId = autoOpenMirror?.mirror?.deviceId;
      if (
        !deviceId ||
        !this.pwaService.isDisplaySupportedByCurrentApp(deviceId) ||
        !this.deviceStorage.ownsDevice(deviceId)
      ) {
        return false;
      }
      this.storage.set("activeMirrorDetails", autoOpenMirror);
      this.route.navigateByUrl("display-view").catch(() => {
        this.route.navigate(["mirrors"]);
      });
      return true;
    } catch (error) {
      console.warn("Unable to auto-open display; using normal startup.", error);
      return false;
    }
  }

  async ngOnInit() {
    // MCP beta entry precedence: browser return relays must consume their URL
    // before normal route startup. Each service declines unrelated requests;
    // only then should normal auto-open/version/navigation behavior run.
    if (await this.mcpBilling.open()) return;
    if (await this.mcpProvider.open()) return;
    // Preserve the requested auth screen rather than redirecting to My Displays.
    if (this.mcpAccountLink.capture()) return;
    if (await this.mcpEmbed.openFromHandoff()) return;
    const autoOpenedDisplay = this.autoOpenOwnedDisplay();

    const urlParams = new URLSearchParams(window.location.search);
    const fbclid = urlParams.get("fbclid");

    if (fbclid) {
      this.conversionCode.push({
        type: "facebook",
        value: {
          fbclid: fbclid,
          fbp: this.getFbp(),
        },
      });
    }

    // Google Ads / Web conversions
    const gclid = urlParams.get("gclid");
    const wbraid = urlParams.get("wbraid");
    const gbraid = urlParams.get("gbraid");

    if (gclid || wbraid || gbraid) {
      this.conversionCode.push({
        type: "google",
        value: {
          gclid: gclid,
          wbraid: wbraid,
          gbraid: gbraid,
        },
      });
    }

    urlParams.forEach((value, key) => {
      if (
        key !== "fbclid" &&
        key !== "gclid" &&
        key !== "wbraid" &&
        key !== "gbraid"
      ) {
        const otherParam = {
          type: key,
          value: { [key]: value },
        };

        this.conversionCode.push(otherParam);
      }
    });

    if (this.conversionCode.length > 0) {
      this.storage.set("conversionCode", this.conversionCode);
    }

    let pageURL = window.location.href;
    const preserveAndroidProfileLink = isAndroidProfileAppLink(
      this.pwaService.getMangoDisplayAppPlatform(),
      window.location.pathname,
    );
    if (!autoOpenedDisplay) {
      if (this.storage.get("userDetails") != null) {
        if (
          !(
            pageURL.includes("widgets/layout?calendar=true") ||
            pageURL.includes("widgets/layout?googlePhotos=true") ||
            pageURL.includes("widgets/layout/outlookcalendar") ||
            pageURL.includes("widgets/layout/powerbi") ||
            pageURL.includes("widgets/layout/outlookTodoTask") ||
            pageURL.includes("widgets/layout/TodoIst") ||
            pageURL.includes("widgets/layout?googletask=true") ||
            pageURL.includes("payment-success") ||
            pageURL.includes("plans-and-payments") ||
            pageURL.includes("user-profile") ||
            pageURL.includes("payment-error") ||
            pageURL.includes("edit-widget") ||
            pageURL.includes("signup?ref=") ||
            preserveAndroidProfileLink
          )
        ) {
          this.route.navigate(["mirrors"]);
        }
      } else {
        this.route.navigate(["login"]);
      }
    }

    // Checks if should display install popup notification:
    if (
      this.deviceService.isMobile() &&
      this.deviceService.os !== "Android" &&
      !this.isInStandaloneMode() &&
      !this.deviceService.isDesktop()
    ) {
    }

    window.addEventListener("message", (event) => {
      if (
        event.origin === this.allowedOrigin &&
        event.data?.type === "openWidgetModal"
      ) {
        this.loadingSpinner.show();
        const token = event.data?.token;
        this.storage.set("userDetails", { authToken: token });

        const userMirrorId = event.data?.userMirrorId;
        const mirrorId = event.data?.mirrorId;

        this.mirrorService
          .getMirror(userMirrorId)
          .pipe(
            tap((data: any) => {
              this.storage.set("activeMirrorDetails", data?.object);
            }),
            tap(() => {
              this.layoutRequest.payload.userMirror.mirror.id = mirrorId;
              this.storage.set("layoutrequest", this.layoutRequest);

              this._dataService.setWidgetSettingsLayout({
                ...this.layoutRequest.payload,
              });

              this._dataService.setWidgetData({
                layoutRequestData: this.layoutRequest.payload,
                widgetId: event.data?.widgetSettingId,
                widgetType: event.data?.widgetType,
              });
            }),
          )
          .subscribe({
            error: (err) => {
              console.error("Failed to get mirror:", err);
            },
            complete: () => {
              this.loadingSpinner.hide();
              this.route.navigateByUrl("/edit-widget");
            },
          });
      }
    });
  }

  isInStandaloneMode = () =>
    "standalone" in window.navigator && this.newVariable.standalone;
}
